import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/api';
import './IntegrationApplicationMasterData.css';

interface ApplicationMaster {
  application_id_code: string;
  application_name: string;
  technology_stack?: string;
  it_hardware_infrastructure?: string;
  application_dependencies?: 'YES' | 'NO';
  recommendation: 'Retire' | 'Re-Platform' | 'Re-Design' | 'Retain';
  created_at_formatted: string;
}

interface ApplicationMasterStats {
  totalApplications: number;
  distribution: {
    'Retire': number;
    'Re-Platform': number;
    'Re-Design': number;
    'Retain': number;
  };
}

interface ApiResponse {
  success: boolean;
  data: ApplicationMaster[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: ApplicationMasterStats;
}

const IntegrationApplicationMasterData: React.FC = () => {
  const [applications, setApplications] = useState<ApplicationMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ApplicationMasterStats>({
    totalApplications: 0,
    distribution: { 'Retire': 0, 'Re-Platform': 0, 'Re-Design': 0, 'Retain': 0 }
  });
  
  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [recommendationFilter, setRecommendationFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [sortField, setSortField] = useState('application_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Track applications assigned to Integration Groups
  const [assignedApplications, setAssignedApplications] = useState<Set<string>>(new Set());

  // Create sorted applications with unassigned apps first
  const sortedApplications = React.useMemo(() => {
    return [...applications].sort((a, b) => {
      const aAssigned = assignedApplications.has(a.application_id_code);
      const bAssigned = assignedApplications.has(b.application_id_code);

      // If assignment status is different, unassigned comes first
      if (aAssigned !== bAssigned) {
        return aAssigned ? 1 : -1; // unassigned (false) < assigned (true)
      }

      // If same assignment status, maintain original order
      return 0;
    });
  }, [applications, assignedApplications]);
  
  // Inline editing state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<'application_dependencies' | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Modal state
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<ApplicationMaster | null>(null);
  

  // Debounce search term
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadApplicationsData = async () => {
    if (!initialLoadDone) setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        search: debouncedSearchTerm,
        recommendationFilter,
        sortBy: sortField,
        sortOrder: sortDirection
      });

      const token = localStorage.getItem('authToken');
      const response = await authFetch(`/api/applications-master?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        // Only throw error for actual server errors, not 404
        if (response.status !== 404) {
          throw new Error('Failed to fetch applications master data');
        }
      }

      const data: ApiResponse = await response.json();

      if (data.success) {
        setApplications(data.data || []);
        setStats(data.stats || {
          totalApplications: 0,
          distribution: { 'Retire': 0, 'Re-Platform': 0, 'Re-Design': 0, 'Retain': 0 }
        });
      } else {
        // Don't treat empty data as an error
        setApplications([]);
        setStats({
          totalApplications: 0,
          distribution: { 'Retire': 0, 'Re-Platform': 0, 'Re-Design': 0, 'Retain': 0 }
        });
      }
    } catch (err: any) {
      console.error('Failed to load applications master data:', err);
      // Only set error for actual network/server errors
      if (err.message && !err.message.includes('404')) {
        setError(err.message || 'Failed to load data');
      }
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  };

  useEffect(() => {
    loadApplicationsData();
    loadAssignedApplications();
  }, [currentPage, debouncedSearchTerm, recommendationFilter, sortField, sortDirection]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handleEditDependency = (applicationId: string, field: 'application_dependencies', currentValue: string) => {
    setEditingId(applicationId);
    setEditingField(field);
    setEditingValue(currentValue || 'NO');
  };

  const handleStartEdit = (applicationId: string) => {
    setEditingId(applicationId);
    setIsEditMode(true);
  };

  const handleCancelEditMode = () => {
    setEditingId(null);
    setEditingField(null);
    setEditingValue('');
    setIsEditMode(false);
  };

  const handleOpenChangeModal = (app: ApplicationMaster) => {
    setSelectedApplication(app);
    setShowChangeModal(true);
  };

  const handleOpenViewModal = (app: ApplicationMaster) => {
    setSelectedApplication(app);
    setShowViewModal(true);
  };

  const handleCloseChangeModal = () => {
    setShowChangeModal(false);
    setSelectedApplication(null);
    // Refresh data after changes
    loadApplicationsData();
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedApplication(null);
  };

  // Function to load applications assigned to Integration Groups
  const loadAssignedApplications = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await authFetch('/api/integration-groups/restricted-applications', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        console.error('Failed to fetch restricted applications');
        return;
      }

      const result = await response.json();
      if (result.success && result.data) {
        // Create a Set of assigned application IDs for fast lookup
        const assignedIds = new Set<string>(result.data.map((app: any) => app.application_id as string));
        setAssignedApplications(assignedIds);
      }
    } catch (error) {
      console.error('Error loading assigned applications:', error);
    }
  };

  const handleSaveDependency = async () => {
    if (!editingId || !editingField) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await authFetch(`/api/applications-master/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          [editingField]: editingValue
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save dependency');
      }

      // Update the local state
      setApplications(prevApps =>
        prevApps.map(app =>
          app.application_id_code === editingId
            ? { ...app, [editingField as string]: editingValue as 'YES' | 'NO' }
            : app
        )
      );

      setEditingId(null);
      setEditingField(null);
      setEditingValue('');
    } catch (error) {
      console.error('Error saving dependency:', error);
      setError('Failed to save dependency');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingField(null);
    setEditingValue('');
    setIsEditMode(false);
  };

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'Retire': return '#dc2626';
      case 'Re-Platform': return '#f59e0b';
      case 'Re-Design': return '#3b82f6';
      case 'Retain': return '#10b981';
      default: return '#6c757d';
    }
  };

  const getRecommendationIcon = (recommendation: string) => {
    switch (recommendation) {
      case 'Retire': return '🗑️';
      case 'Re-Platform': return '🔄';
      case 'Re-Design': return '🎨';
      case 'Retain': return '✅';
      default: return '❓';
    }
  };


  const totalPages = Math.ceil(stats.totalApplications / pageSize);

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Integration Application Master Data</h1>
              <p className="page-header__subtitle">
                Manage applications that have been transferred to integration framework
              </p>
            </div>
          </div>
        </div>
        <div className="master-data-container">
          <div className="md-loading-state">
            <div className="md-spinner"></div>
            <p>Loading applications master data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Integration Application Master Data</h1>
              <p className="page-header__subtitle">
                Manage applications that have been transferred to integration framework
              </p>
            </div>
          </div>
        </div>
        <div className="master-data-container">
          <div className="md-error-state">
            <div className="md-error-icon">⚠️</div>
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button className="md-button--primary" onClick={() => loadApplicationsData()}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Integration Application Master Data</h1>
            <p className="page-header__subtitle">
              Manage applications that have been transferred to integration framework
            </p>
          </div>
          <div className="page-header__actions">
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">


      {/* Filters and Search */}
      <div className="md-card">
        <div className="md-card-content">
          <div className="md-filters-row">
            <div className="md-search-field">
              <input
                type="text"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="md-input"
              />
            </div>
            
            <div className="md-filter-field">
              <select
                value={recommendationFilter}
                onChange={(e) => {
                  setRecommendationFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="md-select"
              >
                <option value="all">All Recommendations</option>
                <option value="Retire">🗑️ Retire</option>
                <option value="Re-Platform">🔄 Re-Platform</option>
                <option value="Re-Design">🎨 Re-Design</option>
                <option value="Retain">✅ Retain</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="md-card">
        <div className="md-table-container">
          {applications.length > 0 ? (
            <table className="md-table">
              <thead>
                <tr>
                  <th
                    className="md-table-header sortable"
                    onClick={() => handleSort('application_id_code')}
                  >
                    Application
                    {sortField === 'application_id_code' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="md-table-header">Assignment Status</th>
                  <th className="md-table-header">Technology</th>
                  <th className="md-table-header">IT Infrastructure</th>
                  <th className="md-table-header">Application Dependencies</th>
                  <th
                    className="md-table-header sortable"
                    onClick={() => handleSort('recommendation')}
                  >
                    Final Recommendation
                    {sortField === 'recommendation' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="md-table-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedApplications.map((app) => (
                  <tr key={app.application_id_code} className="md-table-row">
                    <td className="md-table-cell">
                      <div className="md-app-info">
                        <div className="md-app-info__primary">{app.application_id_code}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="md-app-info__primary">{app.application_name}</span>
                          {(app as any).license_renegotiation && (
                            <span style={{
                              padding: '2px 8px', fontSize: '10px', fontWeight: '600',
                              backgroundColor: '#fff3e0', color: '#e65100', borderRadius: '12px',
                              border: '1px solid #ffcc80', whiteSpace: 'nowrap'
                            }}>
                              License Renegotiation
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="md-table-cell">
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        {assignedApplications.has(app.application_id_code) ? (
                          <>
                            <span style={{
                              fontSize: '14px',
                              backgroundColor: '#FEF3C7',
                              color: '#D97706',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontWeight: 'bold'
                            }}>
                              📊 ASSIGNED
                            </span>
                            {/* Retain and Re-Design apps can be shared across multiple groups */}
                            {(app.recommendation === 'Retain' || app.recommendation === 'Re-Design') && (
                              <span style={{
                                fontSize: '12px',
                                backgroundColor: '#D1FAE5',
                                color: '#059669',
                                padding: '2px 6px',
                                borderRadius: '10px',
                                fontWeight: '500'
                              }}>
                                + AVAILABLE
                              </span>
                            )}
                          </>
                        ) : (
                          <span style={{
                            fontSize: '14px',
                            backgroundColor: '#D1FAE5',
                            color: '#059669',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontWeight: 'bold'
                          }}>
                            ✅ AVAILABLE
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="md-table-cell">
                      <span className="technology-info">
                        {app.technology_stack || 'Not specified'}
                      </span>
                    </td>
                    <td className="md-table-cell">
                      <span className="infrastructure-info">
                        {app.it_hardware_infrastructure || 'Not specified'}
                      </span>
                    </td>
                    <td className="md-table-cell">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {app.application_dependencies === 'YES' ? (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            backgroundColor: '#1976d2',
                            color: 'white',
                            borderRadius: '16px',
                            fontSize: '14px',
                            fontWeight: '600',
                            boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)'
                          }}>
                            <span style={{ fontSize: '16px' }}>✓</span> YES
                          </span>
                        ) : (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            backgroundColor: '#f5f5f5',
                            color: '#666',
                            borderRadius: '16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            border: '1px solid #e0e0e0'
                          }}>
                            <span style={{ fontSize: '16px' }}>—</span> NO
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="md-table-cell">
                      <div
                        className="md-chip"
                        style={{
                          backgroundColor: getRecommendationColor(app.recommendation),
                          color: 'white'
                        }}
                      >
                        {getRecommendationIcon(app.recommendation)} {app.recommendation}
                      </div>
                    </td>
                    <td className="md-table-cell">
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                        {/* Show only View button if application is assigned to Integration Group */}
                        {assignedApplications.has(app.application_id_code) ? (
                          <button
                            onClick={() => handleOpenViewModal(app)}
                            title="View application details (Read-only: Application is assigned to Integration Group)"
                            style={{
                              padding: '4px 6px',
                              backgroundColor: 'transparent',
                              color: '#1976d2',
                              border: '1px solid #1976d2',
                              borderRadius: '4px',
                              fontSize: '14px',
                              cursor: 'pointer',
                              minWidth: 'auto'
                            }}
                          >
                            👁️
                          </button>
                        ) : (
                          // Show both Change and View buttons if not assigned
                          <>
                            <button
                              onClick={() => handleOpenViewModal(app)}
                              title="View application details"
                              style={{
                                padding: '4px 6px',
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                border: '1px solid #1976d2',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                minWidth: 'auto'
                              }}
                            >
                              👁️
                            </button>
                            <button
                              onClick={() => handleOpenChangeModal(app)}
                              title="Change application details"
                              style={{
                                padding: '4px 6px',
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                border: '1px solid #1976d2',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                minWidth: 'auto'
                              }}
                            >
                              ✏️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="md-empty-state">
              <div className="md-empty-icon">📋</div>
              <h3>No Applications Found</h3>
              <p>No applications have been transferred to integration yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="md-pagination">
          <button 
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="md-button--secondary"
          >
            First
          </button>
          <button 
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="md-button--secondary"
          >
            Previous
          </button>
          <span className="md-pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="md-button--secondary"
          >
            Next
          </button>
          <button 
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="md-button--secondary"
          >
            Last
          </button>
        </div>
      )}

      </div>

      {/* Change Modal */}
      {showChangeModal && selectedApplication && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                Change Application Details
              </h2>
              <button
                onClick={handleCloseChangeModal}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                ✕
              </button>
            </div>

            <ChangeApplicationForm
              application={selectedApplication}
              onSave={handleCloseChangeModal}
              onCancel={handleCloseChangeModal}
            />
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedApplication && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                View Application Details
              </h2>
              <button
                onClick={handleCloseViewModal}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                ✕
              </button>
            </div>

            <ViewApplicationDetails application={selectedApplication} />
          </div>
        </div>
      )}
    </div>
  );
};

// Change Application Form Component
interface ChangeApplicationFormProps {
  application: ApplicationMaster;
  onSave: () => void;
  onCancel: () => void;
}

const ChangeApplicationForm: React.FC<ChangeApplicationFormProps> = ({ application, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    application_dependencies: application.application_dependencies || 'NO'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      const response = await authFetch(`/api/applications-master/${application.application_id_code}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to save changes');
      }

      onSave();
    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {error && (
        <div style={{
          backgroundColor: '#fee',
          color: '#c00',
          padding: '12px',
          borderRadius: '4px',
          border: '1px solid #fcc'
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Read-only fields */}
        <div style={{
          padding: '16px',
          backgroundColor: '#f9f9f9',
          borderRadius: '6px',
          border: '1px solid #e0e0e0'
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#666' }}>Application Information (Read-only)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold' }}>Application ID:</span>
            <span>{application.application_id_code}</span>

            <span style={{ fontWeight: 'bold' }}>Application Name:</span>
            <span>{application.application_name}</span>

            <span style={{ fontWeight: 'bold' }}>Technology Stack:</span>
            <span>{application.technology_stack || 'Not specified'}</span>

            <span style={{ fontWeight: 'bold' }}>IT Infrastructure:</span>
            <span>{application.it_hardware_infrastructure || 'Not specified'}</span>

            <span style={{ fontWeight: 'bold' }}>Final Recommendation:</span>
            <span style={{
              padding: '4px 8px',
              borderRadius: '4px',
              backgroundColor: (() => {
                switch (application.recommendation) {
                  case 'Retire': return '#dc2626';
                  case 'Re-Platform': return '#f59e0b';
                  case 'Re-Design': return '#3b82f6';
                  case 'Retain': return '#10b981';
                  default: return '#6c757d';
                }
              })(),
              color: 'white',
              fontWeight: 'bold',
              fontSize: '12px'
            }}>
              {(() => {
                switch (application.recommendation) {
                  case 'Retire': return '🗑️';
                  case 'Re-Platform': return '🔄';
                  case 'Re-Design': return '🎨';
                  case 'Retain': return '✅';
                  default: return '❓';
                }
              })()} {application.recommendation}
            </span>
          </div>
        </div>

        {/* Editable fields */}
        <div style={{
          padding: '16px',
          backgroundColor: '#fff',
          borderRadius: '6px',
          border: '2px solid #007bff'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#007bff' }}>Editable Dependencies</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', alignItems: 'center' }}>
            <label style={{ fontWeight: 'bold' }}>Application Dependencies:</label>
            <select
              value={formData.application_dependencies}
              onChange={(e) => setFormData({ ...formData, application_dependencies: e.target.value as 'YES' | 'NO' })}
              style={{
                padding: '8px',
                border: '2px solid #007bff',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="YES">YES</option>
              <option value="NO">NO</option>
            </select>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={{
            padding: '10px 20px',
            backgroundColor: '#f5f5f5',
            color: '#333',
            border: '1px solid #ddd',
            borderRadius: '4px',
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};

// View Application Details Component
interface ViewApplicationDetailsProps {
  application: ApplicationMaster;
}

const ViewApplicationDetails: React.FC<ViewApplicationDetailsProps> = ({ application }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', alignItems: 'center' }}>
        <span style={{ fontWeight: 'bold' }}>Application ID:</span>
        <span>{application.application_id_code}</span>

        <span style={{ fontWeight: 'bold' }}>Application Name:</span>
        <span>{application.application_name}</span>

        <span style={{ fontWeight: 'bold' }}>Technology Stack:</span>
        <span>{application.technology_stack || 'Not specified'}</span>

        <span style={{ fontWeight: 'bold' }}>IT Infrastructure:</span>
        <span>{application.it_hardware_infrastructure || 'Not specified'}</span>

        <span style={{ fontWeight: 'bold' }}>Application Dependencies:</span>
        <span style={{
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: application.application_dependencies === 'YES' ? '#e8f5e8' : '#f5f5f5',
          color: application.application_dependencies === 'YES' ? '#2d5a2d' : '#666',
          fontWeight: 'bold'
        }}>
          {application.application_dependencies || 'NO'}
        </span>

        <span style={{ fontWeight: 'bold' }}>Final Recommendation:</span>
        <span style={{
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: (() => {
            switch (application.recommendation) {
              case 'Retire': return '#dc2626';
              case 'Re-Platform': return '#f59e0b';
              case 'Re-Design': return '#3b82f6';
              case 'Retain': return '#10b981';
              default: return '#6c757d';
            }
          })(),
          color: 'white',
          fontWeight: 'bold'
        }}>
          {(() => {
            switch (application.recommendation) {
              case 'Retire': return '🗑️';
              case 'Re-Platform': return '🔄';
              case 'Re-Design': return '🎨';
              case 'Retain': return '✅';
              default: return '❓';
            }
          })()} {application.recommendation}
        </span>

        <span style={{ fontWeight: 'bold' }}>License Renegotiation:</span>
        <span style={{
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: (application as any).license_renegotiation ? '#fff3e0' : '#f5f5f5',
          color: (application as any).license_renegotiation ? '#e65100' : '#666',
          fontWeight: 'bold',
          border: (application as any).license_renegotiation ? '1px solid #ffcc80' : 'none'
        }}>
          {(application as any).license_renegotiation ? 'YES — Eligible for License Renegotiation' : 'NO'}
        </span>

        <span style={{ fontWeight: 'bold' }}>Created:</span>
        <span>{application.created_at_formatted}</span>
      </div>
    </div>
  );
};

export default IntegrationApplicationMasterData;